import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import {
  Phone,
  Mail,
  Calendar,
  MessageSquare,
  CalendarCheck,
  FileText,
  CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    businessName: "",
    email: "",
    phone: "",
    employees: "",
    service: "",
    message: "",
    contactMethod: "either"
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const scrollToForm = () => {
    const element = document.getElementById("contact-form");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        company: formData.businessName,
        message: `Service: ${formData.service || 'Not specified'}\nEmployees: ${formData.employees || 'Not specified'}\nPreferred Contact: ${formData.contactMethod}\n\n${formData.message}`
      };

      await axios.post(`${API}/contact`, submitData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Header scrolled={scrolled} />
      
      <main>
        {/* Page Header */}
        <section className="py-16 md:py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="page-header">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="mb-5 text-sm" style={{ color: "#6B7280" }} data-testid="breadcrumb">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <span className="mx-2">›</span>
              <span>Contact</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: "#1F2937" }} data-testid="page-title">
              Get in Touch
            </h1>
            <p className="text-lg" style={{ color: "#4A5568" }}>
              We're here to help. Reach out today.
            </p>
          </div>
        </section>

        {/* Contact Methods Section */}
        <section className="py-20 px-5 bg-white" data-testid="contact-methods">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Phone Card */}
              <div className="rounded-xl p-10 text-center border-2 border-gray-200 transition-all hover:border-blue-400" style={{ backgroundColor: "#F9FAFB" }} data-testid="phone-card">
                <Phone size={56} style={{ color: "#3B82F6" }} className="mx-auto mb-5" />
                <h2 className="text-2xl font-bold mb-4" style={{ color: "#1F2937" }}>Call Us</h2>
                <a href="tel:7783497449" className="text-2xl md:text-3xl font-bold block mb-3 hover:underline" style={{ color: "#3B82F6" }} data-testid="phone-number">
                  (778) 349-7449
                </a>
                <p className="text-base mb-1" style={{ color: "#4A5568" }}>Monday - Friday</p>
                <p className="text-base mb-6" style={{ color: "#4A5568" }}>8:00 AM - 6:00 PM PST</p>
                <a href="tel:7783497449" className="inline-block px-7 py-3 rounded-md text-base font-semibold text-white" style={{ backgroundColor: "#3B82F6" }}>
                  Call Now
                </a>
              </div>

              {/* Email Card */}
              <div className="rounded-xl p-10 text-center border-2 border-gray-200 transition-all hover:border-green-400" style={{ backgroundColor: "#F9FAFB" }} data-testid="email-card">
                <Mail size={56} style={{ color: "#2C5F2D" }} className="mx-auto mb-5" />
                <h2 className="text-2xl font-bold mb-4" style={{ color: "#1F2937" }}>Email Us</h2>
                <a href="mailto:info@a3technologies.com" className="text-lg md:text-xl font-bold block mb-3 hover:underline break-all" style={{ color: "#2C5F2D" }} data-testid="email-address">
                  info@a3technologies.com
                </a>
                <p className="text-base mb-6" style={{ color: "#4A5568" }}>We respond within 24 hours</p>
                <a href="mailto:info@a3technologies.com?subject=IT%20Support%20Inquiry" className="inline-block px-7 py-3 rounded-md text-base font-semibold text-white" style={{ backgroundColor: "#2C5F2D" }}>
                  Send Email
                </a>
              </div>

              {/* Schedule Card */}
              <div className="rounded-xl p-10 text-center border-2 border-gray-200 transition-all hover:border-blue-400" style={{ backgroundColor: "#F9FAFB" }} data-testid="schedule-card">
                <Calendar size={56} style={{ color: "#3B82F6" }} className="mx-auto mb-5" />
                <h2 className="text-2xl font-bold mb-4" style={{ color: "#1F2937" }}>Book a Meeting</h2>
                <p className="text-base mb-6 leading-relaxed" style={{ color: "#4A5568" }}>
                  Schedule a free consultation at a time that works for you
                </p>
                <button onClick={scrollToForm} className="inline-block px-7 py-3 rounded-md text-base font-semibold text-white" style={{ backgroundColor: "#3B82F6" }}>
                  Schedule Consultation
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact-form" className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="contact-form-section">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 md:p-12" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-3" style={{ color: "#1F2937" }}>
                Send Us a Message
              </h2>
              <p className="text-base text-center mb-10" style={{ color: "#4A5568" }}>
                Fill out the form below and we'll get back to you within 24 hours
              </p>

              {submitted ? (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-8 text-center" data-testid="success-message">
                  <CheckCircle size={48} style={{ color: "#10B981" }} className="mx-auto mb-4" />
                  <p className="text-base" style={{ color: "#065F46" }}>
                    Thank you for contacting A3 Technologies! We've received your message and will respond within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  {/* Name Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="firstName" className="block text-sm font-semibold mb-2" style={{ color: "#374151" }}>
                        First Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John"
                        data-testid="input-firstname"
                        className="h-12 border-gray-300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="block text-sm font-semibold mb-2" style={{ color: "#374151" }}>
                        Last Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe"
                        data-testid="input-lastname"
                        className="h-12 border-gray-300"
                      />
                    </div>
                  </div>

                  {/* Business Name */}
                  <div>
                    <Label htmlFor="businessName" className="block text-sm font-semibold mb-2" style={{ color: "#374151" }}>
                      Business Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="businessName"
                      name="businessName"
                      type="text"
                      required
                      value={formData.businessName}
                      onChange={handleChange}
                      placeholder="Your Company Inc."
                      data-testid="input-business"
                      className="h-12 border-gray-300"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="block text-sm font-semibold mb-2" style={{ color: "#374151" }}>
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      data-testid="input-email"
                      className="h-12 border-gray-300"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <Label htmlFor="phone" className="block text-sm font-semibold mb-2" style={{ color: "#374151" }}>
                      Phone Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(778) 123-4567"
                      data-testid="input-phone"
                      className="h-12 border-gray-300"
                    />
                  </div>

                  {/* Number of Employees */}
                  <div>
                    <Label className="block text-sm font-semibold mb-2" style={{ color: "#374151" }}>
                      Number of Employees
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange("employees", value)}>
                      <SelectTrigger className="h-12 border-gray-300" data-testid="select-employees">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10 employees</SelectItem>
                        <SelectItem value="11-25">11-25 employees</SelectItem>
                        <SelectItem value="26-50">26-50 employees</SelectItem>
                        <SelectItem value="50+">50+ employees</SelectItem>
                        <SelectItem value="just-me">Just me</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Service Interested In */}
                  <div>
                    <Label className="block text-sm font-semibold mb-2" style={{ color: "#374151" }}>
                      Service Interested In
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange("service", value)}>
                      <SelectTrigger className="h-12 border-gray-300" data-testid="select-service">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="managed-it">Managed IT Services</SelectItem>
                        <SelectItem value="cybersecurity">Cybersecurity</SelectItem>
                        <SelectItem value="cloud">Cloud Solutions</SelectItem>
                        <SelectItem value="consulting">IT Consulting</SelectItem>
                        <SelectItem value="multiple">Not Sure / Multiple Services</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="block text-sm font-semibold mb-2" style={{ color: "#374151" }}>
                      How can we help? <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your IT needs or challenges..."
                      data-testid="input-message"
                      className="min-h-[120px] border-gray-300"
                    />
                  </div>

                  {/* Preferred Contact Method */}
                  <div>
                    <Label className="block text-sm font-semibold mb-3" style={{ color: "#374151" }}>
                      Preferred contact method
                    </Label>
                    <RadioGroup
                      defaultValue="either"
                      onValueChange={(value) => handleSelectChange("contactMethod", value)}
                      className="flex flex-wrap gap-6"
                      data-testid="radio-contact-method"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="email" id="r-email" />
                        <Label htmlFor="r-email" className="text-base" style={{ color: "#374151" }}>Email</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="phone" id="r-phone" />
                        <Label htmlFor="r-phone" className="text-base" style={{ color: "#374151" }}>Phone</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="either" id="r-either" />
                        <Label htmlFor="r-either" className="text-base" style={{ color: "#374151" }}>Either</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-btn"
                    className="w-full h-14 text-lg font-bold rounded-lg mt-4"
                    style={{ backgroundColor: "#3B82F6" }}
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-5 bg-white" data-testid="faq-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Quick Answers to Common Questions
            </h2>
            <div className="space-y-4">
              <FAQItem q="Do you offer free consultations?" a="Yes! We provide free initial consultations to discuss your needs and how we can help." />
              <FAQItem q="How quickly can you respond to emergencies?" a="For managed service clients, we respond to critical issues immediately. For new inquiries, we'll respond within one business day." />
              <FAQItem q="What areas do you serve?" a="We serve businesses throughout British Columbia and can provide remote support across Canada." />
              <FAQItem q="Do I need to sign a long-term contract?" a="We offer flexible terms. While some clients prefer annual agreements, we can discuss options that work for your business." />
              <FAQItem q="How much do your services cost?" a="Pricing depends on your specific needs. Contact us for a free consultation and custom quote." />
              <FAQItem q="Can you work with our existing systems?" a="Yes! We work with a wide range of technologies and can integrate with your current systems." />
            </div>
          </div>
        </section>

        {/* What Happens Next Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="next-steps">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              What Happens After You Contact Us?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <StepCard num="1" Icon={MessageSquare} title="We Review Your Message" description="We'll review your message and respond within 24 hours" color="#3B82F6" />
              <StepCard num="2" Icon={CalendarCheck} title="Schedule a Consultation" description="We'll schedule a free consultation to discuss your needs" color="#2C5F2D" />
              <StepCard num="3" Icon={FileText} title="Receive Your Proposal" description="We'll provide a custom proposal and answer all your questions" color="#3B82F6" />
            </div>
          </div>
        </section>

        {/* Alternative CTA Section */}
        <section className="py-16 px-5" style={{ background: "linear-gradient(to right, #3B82F6, #2563EB)" }} data-testid="cta-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Prefer to speak with someone right away?
            </h2>
            <a href="tel:7783497449" className="text-4xl md:text-5xl font-bold text-white block mb-4 hover:underline" data-testid="cta-phone">
              (778) 349-7449
            </a>
            <p className="text-base" style={{ color: "rgba(255,255,255,0.8)" }}>
              Monday - Friday, 8:00 AM - 6:00 PM PST
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Helper Components
const FAQItem = ({ q, a }) => (
  <div className="rounded-lg p-6" style={{ backgroundColor: "#F9FAFB" }}>
    <h3 className="text-lg font-bold mb-3" style={{ color: "#1F2937" }}>{q}</h3>
    <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>{a}</p>
  </div>
);

const StepCard = ({ num, Icon, title, description, color }) => (
  <div className="bg-white rounded-xl p-10 text-center" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
    <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-5" style={{ backgroundColor: color }}>
      {num}
    </div>
    <Icon size={40} style={{ color }} className="mx-auto mb-4" />
    <h3 className="text-xl font-bold mb-3" style={{ color: "#1F2937" }}>{title}</h3>
    <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>{description}</p>
  </div>
);

export default ContactPage;
